import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import { 
  CheckCircle, 
  Luggage, 
  MapPin, 
  Calendar, 
  Clock, 
  Euro, 
  MessageCircle, 
  Mail,
  ExternalLink,
  Copy,
  Home
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingConfirmation() {
  const { bookingId } = useParams();
  const { t } = useLanguage();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBooking();
    createConfetti();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (err) {
      setError("Booking not found");
    } finally {
      setLoading(false);
    }
  };

  const createConfetti = () => {
    const colors = ['#004E64', '#FFB400', '#E5A200', '#003848'];
    for (let i = 0; i < 50; i++) {
      setTimeout(() => {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + 'vw';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDuration = (Math.random() * 2 + 2) + 's';
        document.body.appendChild(confetti);
        setTimeout(() => confetti.remove(), 4000);
      }, i * 50);
    }
  };

  const copyBookingId = () => {
    navigator.clipboard.writeText(bookingId);
    toast.success("Booking ID copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4 w-12 h-12 border-4"></div>
          <p className="text-[#5A6B7C]">{t('confirmation.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Luggage className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="font-serif text-3xl font-semibold text-[#004E64] mb-4">{t('confirmation.notFound')}</h1>
          <p className="text-[#5A6B7C] mb-8">{t('confirmation.notFoundDesc')}</p>
          <Link to="/">
            <Button className="bg-[#004E64] hover:bg-[#003848] text-white rounded-full px-8 py-4">
              <Home className="w-5 h-5 mr-2" /> {t('confirmation.backHome')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] py-12 px-6" data-testid="booking-confirmation-page">
      {/* Language Selector */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSelector />
      </div>

      {/* Header */}
      <div className="max-w-2xl mx-auto text-center mb-12">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-slow">
          <CheckCircle className="w-12 h-12 text-green-500" />
        </div>
        <h1 className="font-serif text-4xl md:text-5xl font-semibold text-[#004E64] mb-4" data-testid="confirmation-title">
          {t('confirmation.title')}
        </h1>
        <p className="text-xl text-[#5A6B7C]">{t('confirmation.subtitle')}</p>
      </div>

      {/* Booking Card */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl shadow-[#004E64]/10 overflow-hidden">
          {/* Booking ID Header */}
          <div className="bg-[#004E64] p-6 text-center">
            <p className="text-white/70 text-sm uppercase tracking-widest mb-2">{t('confirmation.reference')}</p>
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-xl text-white" data-testid="booking-id">{bookingId.slice(0, 8).toUpperCase()}</span>
              <button onClick={copyBookingId} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="copy-booking-id">
                <Copy className="w-4 h-4 text-white/70" />
              </button>
            </div>
          </div>

          {/* Booking Details */}
          <div className="p-8">
            {/* Customer Info */}
            <div className="mb-8">
              <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{t('confirmation.customerDetails')}</h3>
              <div className="space-y-3">
                <p className="text-lg text-[#004E64]" data-testid="customer-name">{booking.full_name}</p>
                <p className="flex items-center gap-2 text-[#5A6B7C]">
                  <Mail className="w-4 h-4" /> {booking.email}
                </p>
                <p className="flex items-center gap-2 text-[#5A6B7C]">
                  <MessageCircle className="w-4 h-4" /> {booking.whatsapp_number}
                </p>
              </div>
            </div>

            {/* Storage Details */}
            <div className="mb-8 pt-6 border-t border-[#004E64]/10">
              <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{t('confirmation.storageDetails')}</h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-[#5A6B7C] mb-1">{t('confirmation.numberOfBags')}</p>
                  <p className="text-lg font-semibold text-[#004E64] flex items-center gap-2" data-testid="number-of-bags">
                    <Luggage className="w-5 h-5 text-[#FFB400]" />
                    {booking.number_of_bags} {booking.number_of_bags === 1 ? t('booking.bag') : t('booking.bags')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-[#5A6B7C] mb-1">{t('confirmation.pricingType')}</p>
                  <p className="text-lg font-semibold text-[#004E64] capitalize">{booking.pricing_type === 'daily' ? t('common.daily') : t('common.hourly')}</p>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="mb-8 pt-6 border-t border-[#004E64]/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <p className="text-sm text-[#5A6B7C] mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> {t('confirmation.pickup')}
                  </p>
                  <p className="text-lg font-semibold text-[#004E64]" data-testid="pickup-date">{booking.pickup_date}</p>
                  <p className="text-[#FFB400] font-medium flex items-center gap-1" data-testid="pickup-time">
                    <Clock className="w-4 h-4" /> {booking.pickup_time}
                  </p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-4">
                  <p className="text-sm text-[#5A6B7C] mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> {t('confirmation.dropoff')}
                  </p>
                  <p className="text-lg font-semibold text-[#004E64]" data-testid="dropoff-date">{booking.dropoff_date}</p>
                  <p className="text-[#FFB400] font-medium flex items-center gap-1" data-testid="dropoff-time">
                    <Clock className="w-4 h-4" /> {booking.dropoff_time}
                  </p>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="mb-8 pt-6 border-t border-[#004E64]/10">
              <div className="bg-[#004E64] rounded-xl p-6 flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">{t('confirmation.totalPrice')}</p>
                  <p className="text-white/70 text-sm">{t('confirmation.payAtLocation')}</p>
                </div>
                <p className="font-serif text-4xl font-bold text-[#FFB400]" data-testid="total-price">€{booking.total_price}</p>
              </div>
            </div>

            {/* Location */}
            <div className="pt-6 border-t border-[#004E64]/10">
              <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-[#004E64]/60 mb-4">{t('confirmation.location')}</h3>
              <div className="bg-[#F5F5F0] rounded-xl p-4 mb-4">
                <p className="flex items-center gap-2 text-[#004E64] font-medium">
                  <MapPin className="w-5 h-5 text-[#FFB400]" />
                  {booking.business_address}
                </p>
              </div>
              <a
                href={booking.google_maps_link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#004E64] hover:bg-[#003848] text-white rounded-xl px-6 py-4 font-medium transition-colors"
                data-testid="open-maps-btn"
              >
                <MapPin className="w-5 h-5" />
                {t('confirmation.openMaps')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Actions */}
          <div className="p-8 bg-[#F5F5F0] border-t border-[#004E64]/10">
            <p className="text-center text-[#5A6B7C] mb-4">{t('confirmation.shareWhatsApp')}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={booking.whatsapp_admin_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-xl px-6 py-4 font-medium transition-colors"
                data-testid="whatsapp-contact-btn"
              >
                <MessageCircle className="w-5 h-5" />
                {t('confirmation.contactWhatsApp')}
              </a>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#004E64] hover:text-[#FFB400] font-medium transition-colors" data-testid="back-home-link">
            <Home className="w-5 h-5" />
            {t('confirmation.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
